import React, { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Lot 692 — Supervision › Incidents : aperçu du récap hebdo stabilité (7 derniers jours par version d'app) envoyé
// chaque lundi aux super admins (cloche + e-mail), et bouton pour se l'envoyer tout de suite.
export const StabilityDigestCard = () => {
  const [d, setD] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get("/admin/health/stability-digest").then(({ data }) => setD(data)).catch(() => setD({ rows: [], totals: {} })); }, []);
  const send = () => {
    setSending(true);
    api.post("/admin/health/stability-digest/send")
      .then(({ data }) => toast.success(`Récap envoyé à ${data.notified} super admin(s)${data.sent ? ` · ${data.sent} e-mail(s)` : ""}`))
      .catch(() => toast.error("Envoi impossible"))
      .finally(() => setSending(false));
  };
  const t = d?.totals || {};
  const rows = Array.isArray(d?.rows) ? d.rows : [];
  const topPaths = Array.isArray(d?.top_paths) ? d.top_paths : []; // lot 693 — 3 pages où l'app plante le plus
  const net = d?.network || null; // lot 713 — coupures réseau vues par les membres, comptées à part
  // Lot 736 — d'abord ce que les membres ont VU (plantages au 1er plan + ANR) ; les fermetures système (arrière-plan :
  // mémoire / CPU repris par Android) à part, sans « +766 » alarmant : la semaine précédente est affichée telle quelle.
  const seen = t.seen ?? ((t.crashes || 0) + (t.anr || 0));
  const system = t.system ?? (t.memory || 0);
  const prevSeen = t.prev_seen ?? t.prev_total ?? null;
  const netLine = net ? (
    <div className="text-[12px] mt-2 rounded-lg px-2.5 py-1.5" style={{ background: "var(--yalola-surface-alt)" }} data-testid="stability-digest-network">
      <span className="font-semibold">Coupures réseau (côté appareils) :</span>{" "}
      {net.reports ? (
        <>
          <b>{net.reports}</b> signalement(s) · {net.devices} appareil(s)
          {net.prev_reports != null && <span style={{ color: net.reports > net.prev_reports ? "#B42318" : "#1B7A3D" }}> ({net.reports > net.prev_reports ? "+" : ""}{net.reports - net.prev_reports} vs sem. préc.)</span>}
          {(net.families || []).length > 0 && <> · {net.families.slice(0, 3).map((f) => `${f.label} ${f.count}`).join(" · ")}</>}
          {(net.places || []).length > 0 && <> · lieux : {net.places.slice(0, 3).map((p) => `${p.place} ${p.count}`).join(", ")}</>}
          {(net.net_types || []).length > 0 && <> · liens : {net.net_types.slice(0, 3).map((x) => `${x.net_type} ${x.count}`).join(", ")}</>}
        </>
      ) : <span style={{ color: "#1B7A3D" }}>aucune cette semaine ✅</span>}
      <span style={{ color: "var(--yalola-text-muted)" }}> — côté serveur : {net.server_incidents || 0} incident(s), {net.unexpected_restarts || 0} redémarrage(s) inattendu(s)</span>
    </div>
  ) : null;
  return (
    <div className="yalola-card p-4" data-testid="stability-digest-card">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase font-semibold" style={{ color: "var(--yalola-text-muted)" }}>Récap hebdo stabilité · chaque lundi</span>
        {d && <span className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid="stability-digest-week">semaine {d.week}</span>}
        <button type="button" onClick={send} disabled={sending} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full disabled:opacity-60"
          style={{ background: "var(--yalola-primary)", color: "#fff" }} data-testid="stability-digest-send">
          <Send size={12} /> {sending ? "Envoi…" : "M'envoyer maintenant"}
        </button>
      </div>
      {!d ? <div className="text-[12px] mt-2" style={{ color: "var(--yalola-text-muted)" }}>Chargement…</div> : rows.length === 0 ? (
        <>
          <div className="text-[12px] mt-2" style={{ color: "#1B7A3D" }} data-testid="stability-digest-empty">Aucun plantage, ANR ni fermeture mémoire signalé ces 7 derniers jours ✅</div>
          {netLine}
        </>
      ) : (
        <>
          <div className="text-[12px] mt-2" data-testid="stability-digest-totals">
            <b>{seen}</b> incident(s) vu(s) par les membres sur 7 jours (plantages au 1er plan + ANR{prevSeen != null && <> · sem. préc. : {prevSeen}</>}) · <b>{system}</b> fermeture(s) système en arrière-plan <span style={{ color: "var(--yalola-text-muted)" }}>(mémoire / CPU repris par Android — normal, le membre n'a rien vu)</span> · {t.devices} appareil(s)
          </div>
          {Array.isArray(t.kinds) && t.kinds.length > 0 && (
            <div className="text-[12px] mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="stability-digest-kinds">
              <span style={{ color: "var(--yalola-text-muted)" }}>Composition des incidents vus :</span>
              {t.kinds.map((k) => (
                <span key={k.kind} className="chip text-[11px]" style={{ background: k.kind === "native_crash" || k.kind === "anr" || k.kind === "hang" ? "#FDECEC" : "var(--yalola-surface-alt)", color: k.kind === "native_crash" || k.kind === "anr" || k.kind === "hang" ? "#B42318" : "var(--yalola-text)" }} data-testid={`stability-kind-${k.kind}`}>
                  <b>{k.count}</b> {k.label}
                </span>
              ))}
            </div>
          )}
          {topPaths.length > 0 && (
            <div className="text-[12px] mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="stability-digest-pages">
              <span style={{ color: "var(--yalola-text-muted)" }}>Pages les plus instables :</span>
              {topPaths.map((p, i) => (
                <span key={p.path} className="chip text-[11px] font-mono" style={{ background: i === 0 ? "#FDECEC" : "var(--yalola-surface-alt)", color: i === 0 ? "#B42318" : "var(--yalola-text)" }}
                  title={`${p.devices} appareil(s) · surtout ${p.main_kind}`} data-testid={`stability-page-${i}`}>
                  {p.path} · {p.count}
                </span>
              ))}
            </div>
          )}
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="stability-digest-table">
              <thead><tr style={{ color: "var(--yalola-text-muted)" }}>
                {["Version", "Vus par les membres", "Plantages 1er plan", "ANR", "Système (arrière-plan)", "Appareils", "Vus sem. préc."].map((h) => <th key={h} className="text-left font-semibold py-1 pr-3">{h}</th>)}
              </tr></thead>
              <tbody>
                {rows.map((r) => {
                  const rs = r.seen ?? ((r.foreground_crashes ?? r.crashes ?? 0) + (r.anr || 0));
                  const sys = r.system ?? (r.memory || 0);
                  return (
                    <tr key={`${r.platform}-${r.app_version}`} style={{ borderTop: "1px solid var(--yalola-border)" }} data-testid={`stability-row-${r.platform}-${r.app_version || "unknown"}`}>
                      <td className="py-1 pr-3 font-semibold whitespace-nowrap">{r.label}</td><td className="py-1 pr-3 font-bold" style={{ color: rs > 0 ? "#B42318" : "#1B7A3D" }}>{rs}</td><td className="py-1 pr-3">{r.foreground_crashes}</td>
                      <td className="py-1 pr-3">{r.anr}</td><td className="py-1 pr-3" style={{ color: "var(--yalola-text-muted)" }}>{sys}</td><td className="py-1 pr-3">{r.devices}</td>
                      <td className="py-1" style={{ color: "var(--yalola-text-muted)" }}>{r.prev_seen ?? r.prev_total ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] mt-1.5" style={{ color: "var(--yalola-text-muted)" }} data-testid="stability-digest-legend">
            Vus par les membres = app fermée ou écran blanc au 1er plan + ANR (app bloquée). Système = fermée par Android en arrière-plan pour libérer la mémoire ou le CPU (la 2.4.1 ne distingue pas arrière-plan / 1er plan ; les versions suivantes ne remontent plus celles d'arrière-plan) ou alerte mémoire iPhone.
          </div>
          {netLine}
        </>
      )}
    </div>
  );
};
